/**
 * VoyageTab.js
 *
 * Voyage management tab - the primary operational interface
 *
 * Two states:
 * 1. No active voyage → Show "Start Voyage" prompt + recent voyages
 * 2. Active voyage → Show phase-specific interface
 *
 * Voyage lifecycle:
 * - Pre-Departure: Checklist items, crew selection, passenger count
 * - Underway: Quick log buttons, active operations
 * - Arrival: Completion reminders
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  Ship,
  Anchor,
  ChevronRight,
  Calendar,
  Clock,
  Users,
  Play,
  PlusCircle,
  Settings,
  ClipboardCheck,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
// FT-358: middle-slot actions registered via context; the shell owns the bar.
import { useSetBottomBarActions } from '../../../../contexts/MobileBottomBarActionsContext';

// FT-80: Daily Checks
import { useDailyChecks } from '../../../../hooks/domain/operations/useDailyChecks';
import { useMainLogbook } from '../../../../hooks/domain/operations/useMainLogbook';
import DailyChecksQuickAccess from './ActiveVoyage/DailyChecksQuickAccess';

// Config
import {
  VOYAGE_STATUS,
  VOYAGE_STATUS_LABELS,
  LOG_ENTRY_TYPES,
  OPERATION_TYPES,
  OPERATION_TYPE_LABELS,
  LOGBOOK_ENTRY_TYPES
} from '../../../../config/operationsConfig';

// Sub-components
import StartVoyageFullScreen from './ActiveVoyage/StartVoyageFullScreen';
import PreDeparturePhase from './ActiveVoyage/PreDeparturePhase';
import UnderwayPhase from './ActiveVoyage/UnderwayPhase';
import ArrivalPhase from './ActiveVoyage/ArrivalPhase';
import DraftVoyageBanner from './ActiveVoyage/DraftVoyageBanner';
import VoyageSidebar from './VoyageSidebar';
import VoyageHistoryView from './VoyageHistoryView';
import VoyageLogView from './VoyageLogView';
import VoyageSettingsModal from './VoyageSettingsModal';

// Phase 3: Voyage Templates
import useVoyageTemplates from '../../../../hooks/domain/operations/useVoyageTemplates';
import VoyageTemplatePicker from './VoyageTemplatePicker';
import VoyageTemplateBuilder from './VoyageTemplateBuilder';

import { portLabel } from '../../../../utils/voyage/portLabel';

/**
 * VoyageTab Component
 *
 * @param {Object} context - Tab context from OperationsModule
 */
const VoyageTab = ({ context }) => {
  const { voyage, logbook, vessel, can, showAlert, showConfirm, voyageFullScreen, setVoyageFullScreen } = context;
  const { activeVoyage, draftVoyage, startVoyage, startVoyageAndDepart, getRecentVoyages, getVoyageStats, voyages, updateVoyage, abortDraftVoyage } = voyage;

  // Local state
  const [showStartSheet, setShowStartSheet] = useState(false);
  const [showRecentVoyages, setShowRecentVoyages] = useState(false);
  const [showVoyageHistory, setShowVoyageHistory] = useState(false);
  const [selectedVoyage, setSelectedVoyage] = useState(null);
  const [showSettings, setShowSettings] = useState(false);

  // Phase 3: Template state
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [showTemplateBuilder, setShowTemplateBuilder] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // FT-80: Daily Checks state
  const [showDailyChecks, setShowDailyChecks] = useState(false);

  // Phase 3: Voyage templates hook
  const { templates, templatesByUsage, createTemplate, incrementUsage, applyTemplate } = useVoyageTemplates();

  // FT-80: Daily Checks hooks
  const { getTodaySummary } = useDailyChecks();
  const { addAutoEntry } = useMainLogbook();
  const dailyChecksSummary = getTodaySummary();

  // Get voyage statistics
  const stats = useMemo(() => getVoyageStats(), [getVoyageStats]);

  // Get recent voyages for display
  const recentVoyages = useMemo(() => getRecentVoyages(5), [getRecentVoyages]);

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short'
    });
  };

  // Handle starting a new voyage (V2: uses startVoyageAndDepart for immediate departure)
  // FT-280: accepts and forwards `timeOptions` ({ eventTime, eventTimezone, delayReason })
  // so the canonical departureTime reflects the Master's picked time, not click time.
  const handleStartVoyage = useCallback(async (voyageData, completedChecks, weatherData, fuelData, timeOptions = null) => {
    // FT-202: Pre-arrival is now an inline operation, no state reset needed

    // Use new combined function that creates voyage as UNDERWAY
    // FT-280: 5th positional arg keeps checkConfig slot, 6th is timeOptions.
    const result = await startVoyageAndDepart(voyageData, completedChecks, weatherData, fuelData, null, timeOptions);
    if (result) {
      setShowStartSheet(false);
      // Log voyage start AND departure to main logbook.
      // FT-280: use result.departureTime (picked time) so the logbook timestamp
      // matches the canonical voyage event time, not the click time.
      await logbook.logVoyageEvent({
        eventType: 'let_go',
        voyageId: result.id,
        voyageNumber: result.voyageNumber,
        from: result.from?.name,
        to: result.to?.name,
        crewCount: result.crewCount,
        passengerCount: result.passengerCount,
        departureFuel: result.departureFuel,
        departureWeather: result.departureWeather,
        timestamp: result.departureTime
      });

      // FT-107: Log any compliance overrides to main logbook
      if (voyageData.complianceOverrides && voyageData.complianceOverrides.length > 0) {
        for (const override of voyageData.complianceOverrides) {
          try {
            await logbook.logComplianceOverride({
              voyageId: result.id,
              voyageNumber: result.voyageNumber,
              blockerType: override.blockerType,
              blockerKey: override.blockerKey,
              blockerName: override.blockerName,
              crewId: override.crewId,
              crewName: override.crewName,
              reason: override.reason,
              dispensationRef: override.dispensationRef,
              acknowledgedBy: override.acknowledgedBy,
              wbc3Ref: override.wbc3Ref,
              timestamp: override.timestamp,
              overrideId: override.id
            });
          } catch (err) {
            console.warn('[VoyageTab] Failed to log compliance override:', err);
          }
        }
      }
    }
    return result;
  }, [startVoyageAndDepart, logbook]);

  // Phase 3 Step 5: Handle saving as planned (PRE_DEPARTURE status)
  const handleSaveAsPlanned = useCallback(async (voyageData) => {
    // Use startVoyage which creates voyage in PRE_DEPARTURE status
    const result = await startVoyage(voyageData);
    if (result) {
      setShowStartSheet(false);
      setSelectedTemplate(null);
      // Log to main logbook
      await logbook.logVoyageEvent({
        eventType: 'voyage_planned',
        voyageId: result.id,
        voyageNumber: result.voyageNumber,
        from: result.from?.name,
        to: result.to?.name,
        crewCount: result.crewCount,
        passengerCount: result.passengerCount,
        timestamp: new Date().toISOString()
      });
    }
    return result;
  }, [startVoyage, logbook]);

  // FT-277: DRAFT banner handlers (mirrors VoyageManagementTab.js:235-245).
  const handleResumeDraft = useCallback(() => {
    setShowStartSheet(true);
  }, []);

  const handleDiscardDraft = useCallback((voyageId) => {
    abortDraftVoyage(voyageId, 'user_discarded').catch(err => {
      console.warn('[VoyageTab] abortDraftVoyage failed (non-fatal):', err);
    });
  }, [abortDraftVoyage]);

  // Helper to get voyage duration string
  const getVoyageDuration = (voy) => {
    if (!voy.departureTime || !voy.arrivalTime) return null;
    const hours = Math.round((new Date(voy.arrivalTime) - new Date(voy.departureTime)) / (1000 * 60 * 60) * 10) / 10;
    return `${hours}h`;
  };

  // Helper to get elapsed time for active voyage
  const getElapsedTime = (departureTime) => {
    if (!departureTime) return '00:00';
    const elapsed = Date.now() - new Date(departureTime).getTime();
    const hours = Math.floor(elapsed / (1000 * 60 * 60));
    const minutes = Math.floor((elapsed % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  // Render tappable active voyage card (shown on ALL screen sizes when not in full-screen mode)
  // User taps this to enter full voyage view
  const renderActiveVoyageCard = () => {
    if (!activeVoyage) return null;

    const statusLabel = VOYAGE_STATUS_LABELS[activeVoyage.status] || activeVoyage.status;
    const isUnderway = activeVoyage.status === VOYAGE_STATUS.UNDERWAY;

    return (
      <button
        onClick={() => setVoyageFullScreen?.(true)}
        className="w-full bg-gradient-to-r from-cyan-600 to-cyan-700 rounded-xl p-4 text-white shadow-lg hover:shadow-xl transition-all text-left"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Play className="h-4 w-4" />
            <span className="text-sm font-medium opacity-90">Active Voyage</span>
          </div>
          <span className="px-2 py-0.5 bg-white/20 rounded text-xs font-medium">
            {activeVoyage.voyageNumber || '#---'}
          </span>
        </div>

        <div className="flex items-center gap-2 text-lg font-semibold mb-3">
          <span>{portLabel(activeVoyage.from, '---')}</span>
          <span className="text-cyan-200">→</span>
          <span>{portLabel(activeVoyage.to, '---')}</span>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 text-sm">
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
              isUnderway ? 'bg-green-400/30 text-green-100' : 'bg-amber-400/30 text-amber-100'
            }`}>
              {statusLabel}
            </span>
            {isUnderway && (
              <>
                <span className="flex items-center gap-1 opacity-90">
                  <Users className="h-3.5 w-3.5" />
                  {(activeVoyage.crewCount || 0) + (activeVoyage.passengerCount || 0)} POB
                </span>
                <span className="flex items-center gap-1 opacity-90">
                  <Clock className="h-3.5 w-3.5" />
                  {getElapsedTime(activeVoyage.departureTime)}
                </span>
              </>
            )}
          </div>
          <ChevronRight className="h-5 w-5 opacity-70" />
        </div>
      </button>
    );
  };

  // Render "No Active Voyage" state
  const renderNoActiveVoyage = () => (
    <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
      {/* Standard Slate Header */}
      <div className="bg-slate-700 px-5 py-3 rounded-t-2xl flex items-center justify-between">
        <h3 className="text-base font-semibold text-white flex items-center gap-2">
          <Ship className="h-5 w-5 text-white" />
          Voyage Log
        </h3>
        {/* Settings button - visible on all screens */}
        <button
          onClick={() => setShowSettings(true)}
          className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          aria-label="Voyage Settings"
        >
          <Settings className="h-5 w-5" />
        </button>
      </div>

      <div className="p-6">
        {/* FT-160: Daily Checks + Start Voyage side by side */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          {/* Daily Checks Card */}
          <button
            onClick={() => setShowDailyChecks(true)}
            className={`
              flex items-center gap-4 p-4 rounded-xl border-2 transition-all h-full
              ${dailyChecksSummary.percentage === 100
                ? 'border-green-300 bg-green-50 hover:bg-green-100'
                : dailyChecksSummary.completed > 0
                  ? 'border-amber-300 bg-amber-50 hover:bg-amber-100'
                  : 'border-slate-200 bg-white hover:border-cyan-300 hover:bg-cyan-50'
              }
            `}
          >
            {/* Icon with status indicator */}
            <div className={`
              p-3 rounded-xl shrink-0
              ${dailyChecksSummary.percentage === 100
                ? 'bg-green-200'
                : dailyChecksSummary.completed > 0
                  ? 'bg-amber-200'
                  : 'bg-slate-100'
              }
            `}>
              {dailyChecksSummary.percentage === 100 ? (
                <CheckCircle className="h-6 w-6 text-green-700" />
              ) : dailyChecksSummary.completed > 0 ? (
                <AlertCircle className="h-6 w-6 text-amber-700" />
              ) : (
                <ClipboardCheck className="h-6 w-6 text-slate-600" />
              )}
            </div>

            {/* Label and progress */}
            <div className="flex-1 text-left min-w-0">
              <h3 className={`font-semibold ${
                dailyChecksSummary.percentage === 100
                  ? 'text-green-800'
                  : dailyChecksSummary.completed > 0
                    ? 'text-amber-800'
                    : 'text-slate-800'
              }`}>
                Daily Checks
              </h3>
              <p className={`text-sm ${
                dailyChecksSummary.percentage === 100
                  ? 'text-green-600'
                  : dailyChecksSummary.completed > 0
                    ? 'text-amber-600'
                    : 'text-slate-500'
              }`}>
                {dailyChecksSummary.percentage === 100
                  ? 'All complete'
                  : `${dailyChecksSummary.completed}/${dailyChecksSummary.total} done`
                }
              </p>
            </div>

            {/* Progress badge */}
            <div className={`
              px-2.5 py-1 rounded-full text-sm font-semibold shrink-0
              ${dailyChecksSummary.percentage === 100
                ? 'bg-green-200 text-green-800'
                : dailyChecksSummary.completed > 0
                  ? 'bg-amber-200 text-amber-800'
                  : 'bg-slate-200 text-slate-600'
              }
            `}>
              {dailyChecksSummary.percentage}%
            </div>
          </button>

          {/* Start Voyage Card */}
          <button
            onClick={() => {
              if (templates.length > 0) {
                setShowTemplatePicker(true);
              } else {
                setShowStartSheet(true);
              }
            }}
            disabled={activeVoyage}
            className={`
              flex items-center gap-4 p-4 rounded-xl border-2 transition-all h-full
              ${activeVoyage
                ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
                : 'border-cyan-300 bg-cyan-50 hover:bg-cyan-100 hover:border-cyan-400'
              }
            `}
          >
            {/* Icon */}
            <div className={`p-3 rounded-xl shrink-0 ${
              activeVoyage ? 'bg-gray-200' : 'bg-cyan-200'
            }`}>
              <Ship className={`h-6 w-6 ${
                activeVoyage ? 'text-gray-500' : 'text-cyan-700'
              }`} />
            </div>

            {/* Label */}
            <div className="flex-1 text-left min-w-0">
              <h3 className={`font-semibold ${
                activeVoyage ? 'text-gray-500' : 'text-cyan-800'
              }`}>
                Start Voyage
              </h3>
              <p className={`text-sm ${
                activeVoyage ? 'text-gray-400' : 'text-cyan-600'
              }`}>
                {activeVoyage ? 'Voyage in progress' : 'Begin new voyage'}
              </p>
            </div>

            {/* Arrow */}
            <ChevronRight className={`h-5 w-5 shrink-0 ${
              activeVoyage ? 'text-gray-400' : 'text-cyan-600'
            }`} />
          </button>
        </div>

        {/* Recent Voyages - FT-160: Taller rows like crew list */}
        <div className="border-t border-gray-200 pt-6">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-gray-900">Recent Voyages</h4>
            <button
              onClick={() => setShowVoyageHistory(true)}
              className="text-sm text-cyan-600 hover:text-cyan-700 font-medium"
            >
              View All
            </button>
          </div>

          {recentVoyages.length > 0 ? (
            <>
              {/* Desktop — table rows */}
              <div className="hidden md:block bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-2">Voyage</th>
                      <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-2">Route</th>
                      <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-2">Date</th>
                      <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-2">Duration</th>
                      <th className="px-4 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {recentVoyages.map((voy) => (
                      <tr
                        key={voy.id}
                        onClick={() => setSelectedVoyage(voy)}
                        className="hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{voy.voyageNumber || '—'}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{portLabel(voy.from)} → {portLabel(voy.to)}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{formatDate(voy.departureTime)}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{getVoyageDuration(voy) || '—'}</td>
                        <td className="px-4 py-3 text-right"><ChevronRight className="h-4 w-4 text-gray-400" /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile — cards */}
              <div className="md:hidden space-y-2">
                {recentVoyages.map((voy) => (
                  <button
                    key={voy.id}
                    onClick={() => setSelectedVoyage(voy)}
                    className="w-full bg-white rounded-lg border border-gray-200 p-4 text-left hover:border-gray-300 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-900">{voy.voyageNumber || '—'}</span>
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    </div>
                    <div className="text-sm text-gray-800">{portLabel(voy.from)} → {portLabel(voy.to)}</div>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      <span>{formatDate(voy.departureTime)}</span>
                      {getVoyageDuration(voy) && <span>{getVoyageDuration(voy)}</span>}
                    </div>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-8 text-gray-500 bg-white rounded-lg border border-gray-200">
              <Ship className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No voyages yet</p>
              <p className="text-xs text-gray-400 mt-1">Start your first voyage above</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Render active voyage phase
  const renderActiveVoyage = () => {
    switch (activeVoyage.status) {
      case VOYAGE_STATUS.PRE_DEPARTURE:
        return (
          <PreDeparturePhase
            context={context}
            voyage={activeVoyage}
          />
        );

      case VOYAGE_STATUS.UNDERWAY:
        return (
          <UnderwayPhase
            context={context}
            voyage={activeVoyage}
            onClose={() => context.setVoyageFullScreen?.(false)}
          />
        );

      case VOYAGE_STATUS.ARRIVAL:
        return (
          <ArrivalPhase
            context={context}
            voyage={activeVoyage}
          />
        );

      default:
        return null;
    }
  };

  // Determine what to show on mobile
  // - If voyageFullScreen AND activeVoyage: show full voyage view
  // - If activeVoyage but NOT voyageFullScreen: show card + recent voyages
  // - If no activeVoyage: show no active voyage state
  const showMobileCard = activeVoyage && !voyageFullScreen;

  // FT-358: register middle-slot bottom-bar actions per brief §6
  // (Voyage tab: [+ New Voy.] [History]). Silent no-op while a sub-view
  // (start sheet / history / settings) is open OR while the active voyage
  // is running full-screen (FloatingActionBar takes over — brief §2#9).
  const isSubView =
    showStartSheet || showVoyageHistory || showSettings || voyageFullScreen;
  useSetBottomBarActions(
    isSubView
      ? []
      : [
          {
            key: 'voyage-new',
            icon: PlusCircle,
            label: 'New Voy.',
            primary: true,
            onClick: () => setShowStartSheet(true),
          },
          {
            key: 'voyage-history',
            icon: Clock,
            label: 'History',
            onClick: () => setShowVoyageHistory(true),
          },
        ],
  );

  return (
    <div className="h-full">
      {showStartSheet ? (
        <StartVoyageFullScreen
          context={context}
          onStart={handleStartVoyage}
          onSaveAsPlanned={handleSaveAsPlanned}
          onCancel={() => {
            setShowStartSheet(false);
            setSelectedTemplate(null); // Clear template on cancel
          }}
          template={selectedTemplate}  // Phase 3: Pass template for pre-fill
        />
      ) : showVoyageHistory ? (
        <VoyageHistoryView
          voyages={voyages || []}
          vesselName={vessel?.name || 'Unknown Vessel'}
          onClose={() => setShowVoyageHistory(false)}
          onSelectVoyage={(voy) => {
            setSelectedVoyage(voy);
            setShowVoyageHistory(false);
          }}
        />
      ) : (
        /* Standard 2/3 + 1/3 Grid Layout */
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content - 2/3 width */}
          <div className="lg:col-span-2 pb-20 lg:pb-0">
            {/* FT-277: DRAFT banner sibling — visible whenever a draft exists. */}
            {draftVoyage && (
              <div className="mb-4">
                <DraftVoyageBanner
                  draftVoyage={draftVoyage}
                  onResume={handleResumeDraft}
                  onDiscard={handleDiscardDraft}
                />
              </div>
            )}

            {/* Show card/list view when NOT in full-screen mode (mobile AND desktop) */}
            {showMobileCard && (
              <div className="space-y-6">
                {renderActiveVoyageCard()}
                {/* Also show recent voyages below the card */}
                {renderNoActiveVoyage()}
              </div>
            )}

            {/* Show full voyage phase when in full-screen mode OR no active voyage */}
            {!showMobileCard && (
              <div className="pb-20 lg:pb-0">
                {activeVoyage ? renderActiveVoyage() : renderNoActiveVoyage()}
              </div>
            )}
          </div>

          {/* Sidebar - 1/3 width - Hidden on mobile */}
          <div className="hidden lg:block">
            <VoyageSidebar
              stats={stats}
              activeVoyage={activeVoyage}
              onStartVoyage={() => setShowStartSheet(true)}
              onViewHistory={() => setShowVoyageHistory(true)}
              onSettings={() => setShowSettings(true)}
            />
          </div>
        </div>
      )}

      {/* Voyage Log View - Full screen with scroll */}
      {selectedVoyage && (
        <div className="fixed inset-0 bg-gray-50 z-50 overflow-auto">
          <VoyageLogView
            voyage={selectedVoyage}
            vesselName={vessel?.name || 'Unknown Vessel'}
            onClose={() => setSelectedVoyage(null)}
            onUpdateVoyage={async (updatedVoyage) => {
              // FT-196: Update voyage in storage and refresh local state
              const success = await updateVoyage(updatedVoyage);
              if (success) {
                // Update the selectedVoyage state to reflect changes
                setSelectedVoyage(updatedVoyage);
              }
              return success;
            }}
            onLogSignOff={async (signOffData) => {
              // FT-196: Log voyage sign-off to main logbook
              await addAutoEntry({
                type: LOGBOOK_ENTRY_TYPES.SIGN_OFF,
                timestamp: signOffData.signedAt,
                title: 'Voyage Signed Off',
                summary: `${signOffData.voyageNumber} ${signOffData.route} signed by ${signOffData.signedBy}`,
                data: {
                  voyageId: signOffData.voyageId,
                  voyageNumber: signOffData.voyageNumber,
                  route: signOffData.route,
                  signedBy: signOffData.signedBy,
                  signedByRole: signOffData.signedByRole,
                  signatureType: 'voyage_signoff'
                }
              });
            }}
          />
        </div>
      )}

      {/* Voyage Settings Modal */}
      <VoyageSettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
      />

      {/* Phase 3: Template Picker */}
      {showTemplatePicker && (
        <VoyageTemplatePicker
          templates={templatesByUsage}
          onSelectTemplate={(template) => {
            const voyageData = applyTemplate(template.id);
            setSelectedTemplate(voyageData);
            setShowTemplatePicker(false);
            setShowStartSheet(true);
          }}
          onStartFresh={() => {
            setSelectedTemplate(null);
            setShowTemplatePicker(false);
            setShowStartSheet(true);
          }}
          onManageTemplates={() => {
            setShowTemplatePicker(false);
            setShowTemplateBuilder(true);
          }}
          onClose={() => setShowTemplatePicker(false)}
        />
      )}

      {/* Phase 3: Template Builder */}
      {showTemplateBuilder && (
        <VoyageTemplateBuilder
          onSave={(data) => {
            createTemplate(data);
            setShowTemplateBuilder(false);
          }}
          onCancel={() => setShowTemplateBuilder(false)}
        />
      )}

      {/* FT-80: Daily Checks Quick Access Modal */}
      {showDailyChecks && (
        <DailyChecksQuickAccess
          autoOpen={true}
          onClose={() => setShowDailyChecks(false)}
          // FT-80 / FT-261 (2026-05-31): removed the onLogEntry callback that
          // called addAutoEntry('DAILY_CHECK', {...}). That call was malformed
          // — addAutoEntry's signature is addAutoEntry(entryData) (single object),
          // so passing 'DAILY_CHECK' as the first arg made entryData a string,
          // which fell through to the fallback title 'Entry' and sourceModule
          // 'unknown' (useMainLogbook.js:237/240). This created a malformed
          // duplicate log entry alongside the proper one. DailyChecksQuickAccess
          // already calls logDailyCheck() internally which writes the canonical
          // daily_check entry to the main logbook — no duplicate write needed
          // from the voyage path.
          // Runtime evidence: Slemish ops_logbook 2026-05-31T07:08:24.295/297Z.
        />
      )}
    </div>
  );
};

export default VoyageTab;
